import React, { useState, useEffect } from "react";
import "../Styles/dashboardfrontpage.css";
import { PieChart, Pie, Cell } from "recharts";
import { ArrowRight } from "lucide-react";

// Live site API
const API_BASE_URL = "https://vanakkamhrd.com";

// Which event the Event Summary and Recent Registrations show:
// "festival" = TN HR Summit 2026, "confluence" = India HR Confluence 2026
const SUMMARY_EVENT = "festival";

const quickActions = [
  {
    title: "Upload Event Photos",
    subtitle: "Add a new photos to the gallery",
    icon: "/assets/upload.png",
    isImage: true,
    bg: "#FDECE3",
    fg: "#F0703C",
    arrowBg: "#F0703C",
    page: "gallery",
  },
  {
    title: "Community Members",
    subtitle: "Manage your HR community",
    icon: "/assets/People.png",
    isImage: true,
    bg: "#E4F8ED",
    fg: "#22B573",
    arrowBg: "#22B573",
    page: "community",
  },
  {
    title: "View Subscribers",
    subtitle: "Manage your Subscribers",
    icon: "/assets/sel.png",
    isImage: true,
    bg: "#E5EEFF",
    fg: "#3B6FEA",
    arrowBg: "#3B6FEA",
    page: "subscribers",
  },
  {
    title: "Event Registrations",
    subtitle: "See all sign-ups",
    icon: "/assets/Registration.png",
    isImage: true,
    bg: "#F1E9FE",
    fg: "#8B5CF6",
    arrowBg: "#8B5CF6",
    page: "festival",
  },
];

const isImageFile = (p) => /\.(jpg|jpeg|png)$/i.test(p || "");

export default function AdminDashboard({ onNavigate }) {
  // ---- Live counts fetched from backend ----
  const [counts, setCounts] = useState({
    communityMembers: 0,
    collaborators: 0,
    subscribers: 0,
  });
  const [summary, setSummary] = useState({
    total: 0,
    paid: 0,
    unpaid: 0,
    recent: [],
  });
  const [loadingCounts, setLoadingCounts] = useState(true);

  useEffect(() => {
    const getJson = async (url) => {
      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        return await res.json();
      } catch (e) {
        console.error("Dashboard fetch failed:", url, e);
        return null;
      }
    };

    const load = async () => {
      const [communityData, collabData, subData, summaryData] = await Promise.all([
        getJson(`${API_BASE_URL}/api/community-members`),
                getJson(`${API_BASE_URL}/api/partners`),
        getJson(`${API_BASE_URL}/api/subscribers`),
        getJson(`${API_BASE_URL}/api/registrations/summary?event=${SUMMARY_EVENT}`),
      ]);

      setCounts({
        communityMembers:
          typeof communityData?.total === "number"
            ? communityData.total
            : Array.isArray(communityData?.members)
            ? communityData.members.length
            : 0,
                collaborators: Array.isArray(collabData)
          ? collabData.length
          : Array.isArray(collabData?.partners)
          ? collabData.partners.length
          : typeof collabData?.total === "number"
          ? collabData.total
          : 0,
        subscribers: Array.isArray(subData) ? subData.length : 0,
      });

      if (summaryData) {
        setSummary({
          total: summaryData.total || 0,
          paid: summaryData.paid || 0,
          unpaid: summaryData.unpaid || 0,
          recent: Array.isArray(summaryData.recent) ? summaryData.recent : [],
        });
      }

      setLoadingCounts(false);
    };

    load();
  }, []);

  // ---- Stat cards pull live values from state ----
  const stats = [
    {
      label: "Total Community Members",
      value: loadingCounts ? "..." : counts.communityMembers.toLocaleString(),
      icon: "/assets/Management.png",
      isImage: true,
      bg: "#FDECE3",
      fg: "#F0703C",
    },
    {
      label: "Total Collaborators",
      value: loadingCounts ? "..." : counts.collaborators.toLocaleString(),
      icon: "/assets/coll.png",
      isImage: true,
      bg: "#F1E9FE",
      fg: "#8B5CF6",
    },
    {
      label: "Total Subscribers",
      value: loadingCounts ? "..." : counts.subscribers.toLocaleString(),
      icon: "/assets/su.png",
      isImage: true,
      bg: "#E5EEFF",
      fg: "#3B6FEA",
    },
    {
      label: "TN HR Summit Registrations",
      value: loadingCounts ? "..." : summary.total.toLocaleString(),
      icon: "/assets/reg.png",
      isImage: true,
      bg: "#E4F8ED",
      fg: "#22B573",
    },
  ];

  // Donut data: Paid (green) + Pending (orange). Grey ring when there is no data yet.
  const hasData = summary.paid + summary.unpaid > 0;
  const donutData = hasData
    ? [
        { name: "Paid", value: summary.paid, color: "#22C55E" },
        { name: "Pending", value: summary.unpaid, color: "#FF6A00" },
      ]
    : [{ name: "None", value: 1, color: "#E5E7EB" }];

  return (
    <div className="vhrd">
      <div className="welcome">
        <h2>Welcome to the Admin Dashboard!</h2>
        <p>
          Welcome back to Vanakkam HRD Administration. Manage community
          members, event registrations and gallery from one place.
        </p>
      </div>

      <div className="stat-grid">
        {stats.map((s) => (
          <div className="front-stat-card" key={s.label}>
            <div className="stat-icon" style={{ background: s.bg }}>
              {s.isImage ? (
                <img src={s.icon} alt={s.label} className="stat-icon-img" />
              ) : (
                <s.icon style={{ color: s.fg }} />
              )}
            </div>
            <div>
              <div className="stat-value">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="panel quick-panel">
        <div className="section-title">Quick Actions</div>
        <div className="quick-grid">
          {quickActions.map((q) => (
            <div className="quick-card" key={q.title}>
              <div className="quick-icon" style={{ background: q.bg }}>
                <img src={q.icon} alt={q.title} className="quick-icon-img" />
              </div>
              <div className="quick-row">
                <div>
                  <p className="quick-title">{q.title}</p>
                  <p className="quick-subtitle">{q.subtitle}</p>
                </div>
                <div
                  className="quick-arrow"
                  style={{ background: q.arrowBg, cursor: "pointer" }}
                  onClick={() => onNavigate && onNavigate(q.page)}
                >
                  <ArrowRight />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bottom-row">
        {/* ---------- Event Summary ---------- */}
        <div className="panel">
          <h3>Event Summary</h3>
          <div className="donut-wrap">
            <PieChart width={180} height={180}>
              <Pie
                data={donutData}
                dataKey="value"
                innerRadius={58}
                outerRadius={82}
                startAngle={90}
                endAngle={-270}
                stroke="none"
              >
                {donutData.map((d) => (
                  <Cell key={d.name} fill={d.color} />
                ))}
              </Pie>
            </PieChart>

            <div
              className="donut-center"
              style={{ marginTop: -130, marginBottom: 60 }}
            >
              <div style={{ fontSize: 11, color: "var(--muted)" }}>Total</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>
                {loadingCounts ? "..." : summary.total.toLocaleString()}
              </div>
            </div>

            {/* Legend */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 20,
                fontSize: 13,
                marginTop: 8,
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#22C55E" }} />
                Paid <span style={{ color: "#6b7086" }}>{summary.paid}</span>
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#FF6A00" }} />
                Pending <span style={{ color: "#6b7086" }}>{summary.unpaid}</span>
              </span>
            </div>
          </div>
        </div>

        {/* ---------- Recent Registrations ---------- */}
        <div className="panel">
          <h3>Recent Registrations</h3>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Company</th>
                <th>Designation</th>
                <th>Contact</th>
                <th>Payment Status</th>
              </tr>
            </thead>
            <tbody>
              {summary.recent.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: 20, textAlign: "center" }}>
                    {loadingCounts ? "Loading..." : "No registrations yet."}
                  </td>
                </tr>
              ) : (
                summary.recent.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div className="person-cell">
                        {isImageFile(r.imagePath) ? (
                          <img
                            src={`${API_BASE_URL}${r.imagePath}`}
                            alt={r.fullName}
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div className="avatar" />
                        )}
                        {r.fullName}
                      </div>
                    </td>
                    <td>{r.company}</td>
                    <td>{r.designation}</td>
                    <td>{r.contact}</td>
                    <td>
                      <span className={`status-pill ${r.isPaid ? "paid" : "unpaid"}`}>
                        {r.isPaid ? "Paid" : "Unpaid"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <button
            type="button"
            className="view-all"
            style={{ cursor: "pointer" }}
            onClick={() => onNavigate && onNavigate("festival")}
          >
            View All Registrations <ArrowRight />
          </button>
        </div>
      </div>
    </div>
  );
}